import React, { lazy, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { showStepSkeleton } from '../../utils/functions';
import Skeleton from '../layout/Skeleton';
import LoaderStep from '../common/LoaderStep';
import { useSelector } from 'react-redux';
import { useComponentsLoader } from '@hooks';
import { useShowMobilePreview } from '../../hooks/useShowMobilePreview';

export const STEP_PARAM_NAME = 'rxcnav';

export default function Step({ currentStep }) {
  const navigate = useNavigate();
  const sentinelRef = useRef<HTMLDivElement | null>(null); // La ref per il div di sentinella
  const [isVisible, setIsVisible] = useState(true); // Stato per la visibilità
  const [hideStep, setHideStep] = useState<boolean>(true);
  const [isScrollable, setIsScrollable] = useState(false);
  const showMobilePreview = useShowMobilePreview();
  const previewSection = useSelector(
    (state: any) => state?.workflow?.backofficeConfig?.previewSection
  );
  const leftComponents = useComponentsLoader(previewSection?.components);
  const isWithFnButton = useSelector(
    (state: any) => state.workflow?.currentStepProceedFn
  );

  // Controlla la visibilità dell'elemento con IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting); // Imposta lo stato di visibilità
      },
      { threshold: 0.1 } // Aggiusta il threshold a seconda di quanto deve essere visibile
    );

    // Aspetta che sentinelRef sia correttamente assegnata
    if (sentinelRef.current) {
      observer.observe(sentinelRef.current); // Osserva il div di sentinella
    } else {
      // Se sentinelRef.current è ancora null, ritenta dopo un breve ritardo
      const retryObserver = setInterval(() => {
        if (sentinelRef.current) {
          observer.observe(sentinelRef.current); // Osserva il div di sentinella
          clearInterval(retryObserver); // Ferma il tentativo di ritentare
        }
      }, 100); // Prova ogni 100ms finché sentinelRef.current non è disponibile
    }

    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current); // Rimuovi l'osservatore quando il componente è smontato
      }
    };
  }, []);

  // Navigation logic
  useEffect(() => {
    if (currentStep?.definingAttributes?.title) {
      const key = currentStep.definingAttributes.title;
      const params = new URLSearchParams(location.search);
      if (params.get(STEP_PARAM_NAME) !== key) {
        params.set(STEP_PARAM_NAME, key);
        navigate(
          { search: params.toString() },
          { replace: true, state: 'rxc-configurator-nav' }
        );
      }
    }
  }, [currentStep, location.search, navigate]);

  useEffect(() => {
    showStepSkeleton();
    var div = document.getElementsByClassName('step-container');
    if (div && div[0]) div[0].scrollTop = 0;
  }, [currentStep]);

  const Components = useComponentsLoader(currentStep.components);
  const Nav = Components?.[0];
  const Footer = Components?.[Components?.length - 1];

  useEffect(() => {
    if (hideStep !== window.rxcBrain.hideCurrentStep) {
      setHideStep(window.rxcBrain.hideCurrentStep);
    }
  }, [window.rxcBrain.hideCurrentStep]);

  useEffect(() => {
    if (!hideStep && currentStep) window.rxcBrain.performAnalytics();
  }, [currentStep, hideStep]);

  const handleScrollToDiv = () => {
    const container = document.querySelector('.step-container');
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
  };
  useEffect(() => {
    const checkScrollability = () => {
      const container = document.querySelector('.step-container');
      if (container) {
        const scrollable = container.scrollHeight > container.clientHeight;
        setIsScrollable(scrollable);
      }
    };

    // Delay per aspettare il rendering dei componenti
    const timeout = setTimeout(checkScrollability, 100);

    // Ricalcola al resize
    window.addEventListener('resize', checkScrollability);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', checkScrollability);
    };
  }, [currentStep, hideStep]);

  return (
    <>
      <Skeleton />
      {Nav}
      {hideStep ? (
        <LoaderStep />
      ) : (
        <div
          className={`RXC_STEP tw-relative step-container tw-px-4 tw-pt-2 tw-pb-20 lg:tw-px-10 lg:tw-pt-4 tw-overflow-auto ${currentStep?.definingAttributes?.showFrameImage === true ? 'supernova' : ''} ${!!isWithFnButton ? 'fnButton-padding' : ''}`}
          data-element-id={`LensPanel_${currentStep.definingAttributes.analytics_ID}`}
          tabIndex={-1}
        >
          {Array.isArray(showMobilePreview) &&
            showMobilePreview?.length > 0 &&
            showMobilePreview?.find(
              (step: any) => step === currentStep?.definingAttributes?.title
            ) &&
            !window?.rxcBrain?.isLastStep() && (
              <div className="RXC_PREVIEW_SECTION__mobile tw-relative tw-flex-1 tw-w-1/2 tw-max-w-1/2 tw-h-screen tw-hidden lg:tw-flex lg:tw-items-center lg:tw-justify-center tw-bg-BaseGrayUltraLight">
                {leftComponents}
              </div>
            )}
          {Components?.slice(1, -1)}
          {/* Il div sentinelRef */}
          {!isVisible && isScrollable && (
            <button
              onClick={handleScrollToDiv}
              className="RXC_SCROLL_BTN tw-fixed tw-left-3/4"
              aria-label="Scroll to the bottom"
            ></button>
          )}
          <div ref={sentinelRef} className="tw-h-1"></div>
        </div>
      )}
      {Footer}
    </>
  );
}
